import { Navigate, useLocation, useSearchParams } from "react-router-dom";
import { useProfileSafe } from "@/contexts/UserProfileContext";
import { useAdminAccess } from "@/hooks/useAdminAccess";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useRef, type ReactNode } from "react";
import { findEquivalentRoute } from "@/lib/career-hub/segmentRouteEquivalents";
import { logEvent } from "@/utils/telemetry";

type Segment = "school_student" | "college_student" | "working_professional";

interface SegmentGuardProps {
  allow: Segment[];
  children: ReactNode;
  /** Where to redirect if the user's segment is not in `allow` AND no equivalent route exists. Default: /career-hub */
  redirectTo?: string;
}

const isSegment = (v: string | null | undefined): v is Segment =>
  v === "school_student" || v === "college_student" || v === "working_professional";

const readPreview = (search: URLSearchParams): Segment | null => {
  const fromUrl = search.get("previewSegment");
  if (isSegment(fromUrl)) return fromUrl;
  try {
    const fromSession = sessionStorage.getItem("hub_preview_segment");
    if (isSegment(fromSession)) return fromSession;
  } catch {}
  return null;
};

/**
 * Route-level guard that restricts pages to specific user segments.
 *
 * Behaviour:
 * - While the profile is loading, shows a lightweight skeleton.
 * - If unauthenticated or no segment, sends user to /career-hub which routes
 *   them to their correct segment view (or prompts the segment gate).
 * - On segment mismatch:
 *     1. Log a `segment_route_mismatch` analytics event so we can measure
 *        and fix incorrect navigation paths.
 *     2. Redirect to the closest equivalent route on the user's own
 *        segment (via `findEquivalentRoute`), e.g. a college user landing
 *        on /career-hub/exam-prep is sent to /career-hub/placement-prep.
 *     3. If no equivalent exists, fall back to `redirectTo` (/career-hub).
 * - Admins may bypass via ?previewSegment=... so they can QA other journeys.
 */
export const SegmentGuard = ({ allow, children, redirectTo = "/career-hub" }: SegmentGuardProps) => {
  const { profile, isLoading, isAuthenticated } = useProfileSafe();
  const { isAdmin } = useAdminAccess();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const segment = profile?.user_segment as Segment | null | undefined;
  const previewSegment = isAdmin ? readPreview(searchParams) : null;

  // Compute mismatch + redirect target *before* the effect so we can log
  // the same payload we redirect with.
  const previewAllowed = previewSegment && allow.includes(previewSegment);
  const segmentAllowed = segment && allow.includes(segment);
  const blocked =
    !isLoading &&
    isAuthenticated &&
    !previewAllowed &&
    !segmentAllowed;

  const equivalent = blocked && segment
    ? findEquivalentRoute(location.pathname, segment)
    : null;
  const target = equivalent ?? redirectTo;

  // Fire-and-forget telemetry on mismatch, exactly once per visit.
  const loggedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!blocked) return;
    const key = `${location.pathname}|${segment ?? "none"}`;
    if (loggedRef.current === key) return;
    loggedRef.current = key;
    logEvent("segment_route_mismatch", {
      severity: "warn",
      detail: {
        attempted_path: location.pathname,
        attempted_search: location.search || null,
        user_segment: segment ?? null,
        allowed_segments: allow,
        redirected_to: target,
        had_equivalent: Boolean(equivalent),
        admin_preview_attempted: Boolean(previewSegment),
      },
    });
  }, [blocked, location.pathname, location.search, segment, allow, target, equivalent, previewSegment]);

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-10 w-1/2" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  // Admin preview override: lets admins QA other segment surfaces.
  if (previewAllowed) {
    return <>{children}</>;
  }

  if (!segmentAllowed) {
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};
